import moment from "moment-timezone";

const WORK_START_HOUR = 9;
const TIMEZONE = "Asia/Singapore";

const getDateKey = (date) => moment(date).format("YYYY-MM-DD");

const isWeekend = (date) => {
    const day = moment(date).day();
    if (JSON.parse(process.env.REACT_APP_TEST_SERVER || false)) return false;
    return day === 0;
};

const isHoliday = (date, holidaySet) => holidaySet.has(getDateKey(date));

const isWorkingDay = (date, holidaySet) =>
    !isWeekend(date) && !isHoliday(date, holidaySet);

const isWorkingHour = (date, WORK_END_HOUR = 18, minutes = 0, seconds = 0) => {
    const current = moment(date);
    const start = current.clone().hour(WORK_START_HOUR).minute(0).second(0);
    let end = null;
    if (WORK_END_HOUR == null && minutes == null && seconds == null) {
        end = current.clone().add(24, "hours");
    } else {
        end = current.clone().hour(WORK_END_HOUR).minute(minutes).second(seconds);
    }

    return current.isBetween(start, end, null, "[)");
};

const calculateElapsedWorkingTime = (
    startDate,
    now,
    holidaySet,
    WORK_END_HOUR = 18,
    minutes = 0,
    seconds = 0
) => {
    let current = moment(startDate).startOf("day");
    const end = moment(now);
    let elapsed = 0;

    while (current.isBefore(end, "day") || current.isSame(end, "day")) {
        if (isWorkingDay(current, holidaySet)) {
            const workStart = current
                .clone()
                .hour(WORK_START_HOUR)
                .minute(0)
                .second(0);
            let workEnd = null;
            if (WORK_END_HOUR == null && minutes == null && seconds == null) {
                workEnd = current.clone().add(24, "hours");
            } else {
                workEnd = current
                    .clone()
                    .hour(WORK_END_HOUR)
                    .minute(minutes)
                    .second(seconds);
            }
            const actualStart = moment.max(moment(startDate), workStart);
            const actualEnd = moment.min(end, workEnd);

            if (actualEnd.isAfter(actualStart)) {
                elapsed += actualEnd.diff(actualStart);
            }
        }

        current.add(1, "day").startOf("day");
    }

    return elapsed;
};

// The moment a ticket's completion window runs out: `days` working days of
// DURATION hours each, counted from startDate the same way the Timer counts
// elapsed time (so the date shown always agrees with the countdown chip).
export const calculateWorkingDeadline = (
    startDate,
    days = 2,
    holidays = [],
    DURATION = 9,
    WORK_END_HOUR = 18
) => {
    const holidaySet = new Set(holidays.map(getDateKey));
    const start = moment(startDate);
    const current = start.clone().startOf("day");
    let remaining = days * DURATION * 60 * 60 * 1000;

    while (true) {
        if (isWorkingDay(current, holidaySet)) {
            const workStart = current.clone().hour(WORK_START_HOUR).minute(0).second(0);
            const workEnd = current.clone().hour(WORK_END_HOUR).minute(0).second(0);
            const actualStart = moment.max(start, workStart);
            const available = workEnd.diff(actualStart);
            if (available >= remaining) return actualStart.clone().add(remaining, "ms");
            if (available > 0) remaining -= available;
        }
        current.add(1, "day").startOf("day");
    }
};

/**
 * Calculate remaining time for a ticket using business hours logic
 * @param {string} startDate - Ticket creation date
 * @param {number} endHours - SLA hours (e.g., 2)
 * @param {number} DURATION - Hours per day (e.g., 9 for 9 AM - 6 PM)
 * @param {Array} holidays - Array of holiday dates
 * @param {string} ticketStatus - "0" for open, "1" for closed
 * @param {string} endTime - Optional end time for paused tickets
 * @param {number} WORK_END_HOUR - End hour (default 18 for 6 PM)
 * @param {number} minutes - End minutes (default 0)
 * @param {number} seconds - End seconds (default 0)
 * @returns {number} Remaining time in milliseconds (negative if overdue)
 */
export const calculateTicketRemainingTime = (
    startDate,
    endHours = 2,
    DURATION = 9,
    holidays = [],
    ticketStatus = "0",
    endTime = null,
    WORK_END_HOUR = 18,
    minutes = 0,
    seconds = 0
) => {
    const TOTAL_DURATION_MS = endHours * DURATION * 60 * 60 * 1000;
    const holidaySet = new Set(holidays.map(getDateKey));
    const now = moment();

    const shouldPause =
        ticketStatus === "1" || (endTime && moment(endTime).isBefore(now));

    const actualEnd =
        endTime && moment(endTime).isBefore(now) ? moment(endTime) : now;

    const elapsed = calculateElapsedWorkingTime(
        startDate,
        shouldPause ? actualEnd : now,
        holidaySet,
        WORK_END_HOUR,
        minutes,
        seconds
    );

    const remaining = TOTAL_DURATION_MS - elapsed;
    return remaining;
};
